import { useEffect, useState } from 'react';
import { checkPerformanceBudget } from '../../hooks/useWebVitals';

interface PerformanceMetrics {
  lcp: number | null;
  fid: number | null;
  cls: number | null;
  fcp: number | null;
  ttfb: number | null;
  bundleSize: number | null;
}

export function PerformanceMonitor() {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    lcp: null,
    fid: null,
    cls: null,
    fcp: null,
    ttfb: null,
    bundleSize: null
  });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Only show in development
    if (process.env.NODE_ENV !== 'development') return;

    // Check performance budget on load with error handling
    try {
      checkPerformanceBudget();
    } catch (error) {
      console.warn('Performance budget check failed:', error);
    }

    // Listen for Web Vitals updates
    const handleWebVitals = (event: CustomEvent) => {
      const { name, value } = event.detail;
      setMetrics(prev => ({
        ...prev,
        [name.toLowerCase()]: value
      }));
    };

    // Add event listener for custom web vitals events
    window.addEventListener('web-vitals-update' as any, handleWebVitals);

    // Calculate bundle size with error handling
    try {
      if (typeof performance !== 'undefined' && performance.getEntriesByType) {
        const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
        const jsResources = resources.filter(r => r.name && r.name.includes('.js'));
        const totalJSSize = jsResources.reduce((sum, r) => sum + (r.transferSize || 0), 0);
        
        setMetrics(prev => ({
          ...prev,
          bundleSize: totalJSSize
        }));
      }
    } catch (error) {
      console.warn('Bundle size calculation failed:', error);
    }

    return () => {
      window.removeEventListener('web-vitals-update' as any, handleWebVitals);
    };
  }, []);

  // Don't render in production
  if (process.env.NODE_ENV !== 'development') return null;

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getMetricColor = (name: string, value: number | null) => {
    if (value === null) return 'text-gray-400';
    
    const thresholds: Record<string, { good: number; poor: number }> = {
      lcp: { good: 2500, poor: 4000 },
      fid: { good: 100, poor: 300 },
      cls: { good: 0.1, poor: 0.25 },
      fcp: { good: 1800, poor: 3000 },
      ttfb: { good: 800, poor: 1800 }
    };

    const threshold = thresholds[name];
    if (!threshold) return 'text-gray-400';

    if (value <= threshold.good) return 'text-green-500';
    if (value <= threshold.poor) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <button
        onClick={() => setIsVisible(!isVisible)}
        className="bg-blue-600 text-white p-2 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
        title="Performance Monitor"
      >
        📊
      </button>
      
      {isVisible && (
        <div className="absolute bottom-12 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 w-80">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-gray-900 dark:text-white">Performance Metrics</h3>
            <button
              onClick={() => setIsVisible(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">LCP:</span>
              <span className={getMetricColor('lcp', metrics.lcp)}>
                {metrics.lcp ? `${metrics.lcp}ms` : 'Measuring...'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">FID:</span>
              <span className={getMetricColor('fid', metrics.fid)}>
                {metrics.fid ? `${metrics.fid}ms` : 'Measuring...'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">CLS:</span>
              <span className={getMetricColor('cls', metrics.cls)}>
                {metrics.cls !== null ? metrics.cls.toFixed(3) : 'Measuring...'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">FCP:</span>
              <span className={getMetricColor('fcp', metrics.fcp)}>
                {metrics.fcp ? `${metrics.fcp}ms` : 'Measuring...'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">TTFB:</span>
              <span className={getMetricColor('ttfb', metrics.ttfb)}>
                {metrics.ttfb ? `${metrics.ttfb}ms` : 'Measuring...'}
              </span>
            </div>
            
            <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Bundle Size:</span>
                <span className={metrics.bundleSize && metrics.bundleSize > 1000000 ? 'text-red-500' : 'text-green-500'}>
                  {metrics.bundleSize ? formatBytes(metrics.bundleSize) : 'Calculating...'}
                </span>
              </div>
            </div>
            
            <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
              <button
                onClick={() => {
                  checkPerformanceBudget();
                  console.log('Performance metrics:', metrics);
                }}
                className="w-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 py-1 px-2 rounded text-xs hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
              >
                Check Budget
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}